import { useMemo, useState } from "react";
import { CustomButton } from "../../../../atoms/CustomButton";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { SEND_MESSAGE_TYPE } from "../../../../../constants/types";
import {
  useCorrectMessage,
  useEmailPreview,
  useSendMessagesToAll,
  useSendMessagesToUsers,
  useSendMessageToOne,
} from "../../../../../hooks/messages";
import {
  useCreateEntityPreferencesWithSpecialKey,
  useEmailSenderValidationStatus,
  useGetEmailQuota,
  useGetEntity,
  useGetEntityPreferencesByPath,
  useGetEntityPreferencesWithSpecialKey,
  useGetGroupUsersCount,
  useRequestEmailSenderValidation,
} from "../../../../../hooks/entity";
import { useSenderEmailGate } from "../../../../../hooks/useSenderEmailGate";
import { PlanTier, usePermissions } from "../../../../../utils/permissions";
import { Cap, Level } from "../../../../../utils/rbacCatalog";
import { isReservedSenderDomain } from "../../../../../utils/emailSender";
import { IUser } from "../../../../../contexts/types";
import EmailSenderValidationModal from "../../../../modals/EmailSenderValidationModal";
import { PlansModal } from "../../../../modals/PlansModal";
import SenderEmailGateBanner from "../../../../atoms/SenderEmailGateBanner";
import AccessRestrictedModal from "./AccessRestrictedModal";
import { SendContext, PreviewFields } from "./sendContext";

export interface TenantSendContextArgs {
  user: IUser;
  customer_identification: string;
  entity_identification: string;
  entity_user_identification: string;
  toSendMsgUserId: string;
  group_identification?: string;
  users?: Set<string>;
  type: SEND_MESSAGE_TYPE;
  userData?: Record<string, unknown>;
  isEmailBlocked?: boolean;
  show: boolean;
  close: () => void;
}

export const useTenantSendContext = (
  args: TenantSendContextArgs
): SendContext => {
  const {
    user,
    customer_identification,
    entity_identification,
    entity_user_identification,
    toSendMsgUserId,
    group_identification,
    users,
    type,
    isEmailBlocked,
    show,
    close,
  } = args;

  const { t } = useTranslation();
  const permissions = usePermissions(user);

  const isBulkSend = type !== SEND_MESSAGE_TYPE.SINGLE;

  const [showSenderValidationModal, setShowSenderValidationModal] =
    useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const { data: logoData, isSuccess } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "cdn_logo_project_path"
  );

  const { data: entityData } = useGetEntity(
    customer_identification,
    user.entity_identification,
    {
      enabled: isSuccess,
    }
  );

  const { mutate: sendToAll, isLoading: isSendingToAll } =
    useSendMessagesToAll();
  const { mutate: sendToUsers, isLoading: isSendingToUsers } =
    useSendMessagesToUsers();
  const { mutate: sendToOne, isLoading: isSendingToOne } =
    useSendMessageToOne();
  const { mutate: correctMessage } = useCorrectMessage();
  const { mutate: requestEmailPreview, isLoading: isRequesting } =
    useEmailPreview();

  const { data: userFavouriteIntro } = useGetEntityPreferencesWithSpecialKey({
    customer_identification,
    entityIdentification: user.entity_identification,
    userIdentification: user.user_identification,
    key: "fav_intro",
  });

  const { data: userFavouriteSignature } =
    useGetEntityPreferencesWithSpecialKey({
      customer_identification,
      entityIdentification: user.entity_identification,
      userIdentification: user.user_identification,
      key: "fav_signature",
    });

  const { data: userFavouriteTemplate } = useGetEntityPreferencesWithSpecialKey(
    {
      customer_identification,
      entityIdentification: user.entity_identification,
      userIdentification: user.user_identification,
      key: "fav_template",
    }
  );

  const { mutate: createPreferences } =
    useCreateEntityPreferencesWithSpecialKey();

  const { mutate: requestValidationCode, isLoading: isRequestingValidation } =
    useRequestEmailSenderValidation();

  const { data: entityPreferences } = useGetEntityPreferencesByPath(
    customer_identification,
    entity_identification,
    "email_address_sender",
    { enabled: show }
  );

  const { data: senderValidationStatus, isLoading: isSenderStatusLoading } =
    useEmailSenderValidationStatus(
      customer_identification,
      entity_identification,
      {
        enabled: show,
      }
    );

  const { data: emailQuota } = useGetEmailQuota(
    customer_identification,
    entity_identification,
    { enabled: show && isBulkSend }
  );

  const { data: groupUsersTotal } = useGetGroupUsersCount(
    customer_identification,
    entity_identification,
    group_identification,
    {
      enabled: show && type === SEND_MESSAGE_TYPE.ALL && !!group_identification,
    }
  );

  const recipientCount =
    type === SEND_MESSAGE_TYPE.SINGLE
      ? 1
      : type === SEND_MESSAGE_TYPE.MULTI
      ? users?.size ?? 0
      : groupUsersTotal ?? 0;

  const quotaLimit = emailQuota?.limit ?? null;
  const quotaRemaining = emailQuota?.remaining ?? null;
  const quotaExceeded =
    isBulkSend &&
    quotaRemaining !== null &&
    recipientCount > 0 &&
    recipientCount > quotaRemaining;

  const showQuotaInfo =
    !!emailQuota &&
    !!quotaLimit &&
    quotaLimit > 0 &&
    emailQuota.used / quotaLimit >= 0.5;

  const daysUntilQuotaReset = (() => {
    if (!emailQuota?.resets_at) return null;
    const diffMs =
      new Date(emailQuota.resets_at).getTime() - new Date().getTime();
    if (diffMs <= 0) return 1;
    return Math.max(1, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
  })();

  const senderEmail = (
    entityPreferences?.email_address_sender ?? ""
  ).toLowerCase();
  const isDefaultSender = isReservedSenderDomain(senderEmail);
  const senderUnverified =
    !!senderEmail &&
    !isDefaultSender &&
    (isSenderStatusLoading ||
      !senderValidationStatus ||
      senderValidationStatus.is_valid !== true ||
      (senderValidationStatus.email || "").toLowerCase() !== senderEmail);

  const senderGate = useSenderEmailGate(
    customer_identification,
    entity_identification,
    { enabled: show }
  );

  const handleVerifySender = () => {
    const pendingExpiresAt = senderValidationStatus?.code_expires_at
      ? new Date(senderValidationStatus.code_expires_at).getTime()
      : 0;
    if (pendingExpiresAt > Date.now()) {
      setShowSenderValidationModal(true);
      return;
    }
    requestValidationCode(
      {
        customer_identification,
        entity_identification,
        email: entityPreferences?.email_address_sender || undefined,
      },
      {
        onSuccess: () => setShowSenderValidationModal(true),
      }
    );
  };

  const hasMessageAccess = permissions.hasFeatureAccess("SEND_MESSAGES");

  const isSubmitting = isSendingToAll || isSendingToUsers || isSendingToOne;

  const canSend =
    !isEmailBlocked &&
    !quotaExceeded &&
    !senderUnverified &&
    !isDefaultSender &&
    permissions.can(Cap.SEND_EMAIL, Level.WRITE);

  const handleUpgrade = () => {
    close();
    toast.info(t("permissions.upgradeInfo"));
  };

  const submit: SendContext["submit"] = (formData, { onSuccess }) => {
    if (type === SEND_MESSAGE_TYPE.SINGLE) {
      sendToOne(
        {
          customer_identification: entity_user_identification,
          entity_identification,
          toSendMsgUserId,
          data: formData,
        },
        { onSuccess }
      );
      return;
    }

    if (type === SEND_MESSAGE_TYPE.MULTI) {
      const encodedUsers = JSON.stringify(Array.from(users ?? []));
      formData.set("users", encodedUsers);
    }

    const payload = {
      customer_identification: entity_user_identification,
      entity_identification,
      group_identification,
      data: formData,
    };

    if (type === SEND_MESSAGE_TYPE.MULTI) {
      sendToUsers(payload, { onSuccess });
    } else {
      sendToAll(payload, { onSuccess });
    }
  };

  const preview = (fields: PreviewFields): Promise<string> =>
    new Promise<string>((resolve, reject) => {
      requestEmailPreview(
        {
          entity_user_identification,
          entity_identification,
          template: fields.template,
          data: {
            projectLogo: logoData?.cdn_logo_project_path,
            projectName: entityData?.entity_name,
            title: fields.subject,
            messageText: fields.contentHtml,
            intro: fields.intro,
            signature: fields.signatureHtml,
          },
        },
        {
          onSuccess: (response) => {
            resolve(response?.data ?? "");
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });

  const correct = (html: string): Promise<string | null> =>
    new Promise<string | null>((resolve, reject) => {
      correctMessage(
        {
          customer_identification: entity_user_identification,
          entity_identification,
          message: html,
        },
        {
          onSuccess: (value) => {
            resolve(value?.data?.element?.answer ?? null);
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });

  const saveDefault: SendContext["saveDefault"] = (key, value) => {
    if (!value) return;

    const successToastKey =
      key === "fav_intro"
        ? "dashboard.entities.viewEntityUser.sendMsg.introSavedAsDefault"
        : key === "fav_signature"
        ? "dashboard.entities.viewEntityUser.sendMsg.signatureSavedAsDefault"
        : "dashboard.entities.viewEntityUser.sendMsg.templateSavedAsDefault";

    createPreferences(
      {
        customer_identification,
        entityIdentification: user.entity_identification,
        userIdentification: user.user_identification,
        key,
        value,
      },
      {
        onSuccess: () => {
          toast.success(t(successToastKey));
        },
        onError: () => {
          toast.error(
            t("dashboard.entities.viewEntityUser.sendMsg.failedToSaveDefault")
          );
        },
      }
    );
  };

  const favorites = useMemo(
    () => ({
      intro: userFavouriteIntro?.user_data_preferences?.fav_intro as
        | string
        | undefined,
      signature: userFavouriteSignature?.user_data_preferences
        ?.fav_signature as string | undefined,
      template: userFavouriteTemplate?.user_data_preferences?.fav_template as
        | string
        | undefined,
    }),
    [userFavouriteIntro, userFavouriteSignature, userFavouriteTemplate]
  );

  const banners = (
    <>
      {isEmailBlocked && (
        <div className="rounded-lg bg-red-50 p-4 border border-red-200 mb-4">
          <p className="text-sm text-red-800 font-medium">
            {t(
              "dashboard.entities.viewEntityUser.sendMsg.communicationBlocked"
            )}
          </p>
        </div>
      )}

      {senderUnverified && (
        <div className="rounded-lg bg-red-50 p-2 border border-red-200 mb-4 flex flex-col sm:flex-row sm:items-start gap-3">
          <p className="flex-1 text-sm text-red-800 font-medium">
            {t("dashboard.entities.viewEntityUser.sendMsg.senderNotVerified")}
          </p>
          <CustomButton
            variant="primary"
            onClick={handleVerifySender}
            disabled={isRequestingValidation}
            className="w-full shrink-0 whitespace-nowrap sm:w-auto"
            title={
              isRequestingValidation
                ? t("dashboard.entities.emailSenderValidation.sending")
                : t("dashboard.entities.emailSenderValidation.btnVerifyNow")
            }
          />
        </div>
      )}

      {isDefaultSender && (
        <SenderEmailGateBanner gate={senderGate} className="mb-4" />
      )}

      {isBulkSend &&
        quotaLimit !== null &&
        emailQuota &&
        (quotaExceeded || showQuotaInfo) && (
          <div
            className={`rounded-lg p-4 border mb-4 flex flex-col sm:flex-row sm:items-start gap-3 ${
              quotaExceeded
                ? "bg-red-50 border-red-200"
                : "bg-blue-50 border-blue-200"
            }`}
          >
            <p
              className={`flex-1 text-sm font-medium ${
                quotaExceeded ? "text-red-800" : "text-blue-800"
              }`}
            >
              {quotaExceeded
                ? t(
                    "dashboard.entities.viewEntityUser.sendMsg.quota.exceeded",
                    {
                      recipients: recipientCount,
                      used: emailQuota.used,
                      limit: quotaLimit,
                      remaining: quotaRemaining,
                      days: daysUntilQuotaReset ?? 1,
                    }
                  )
                : t("dashboard.entities.viewEntityUser.sendMsg.quota.info", {
                    remaining: quotaRemaining,
                    limit: quotaLimit,
                  })}
            </p>
            {quotaExceeded && (
              <CustomButton
                variant="primary"
                onClick={() => setShowPlansModal(true)}
                className="w-full shrink-0 whitespace-nowrap sm:w-auto"
                title={t(
                  "dashboard.entities.viewEntityUser.sendMsg.quota.upgradeCta"
                )}
              />
            )}
          </div>
        )}
    </>
  );

  const modals = (
    <>
      <EmailSenderValidationModal
        show={showSenderValidationModal}
        onClose={() => setShowSenderValidationModal(false)}
        pendingEmail={entityPreferences?.email_address_sender || null}
      />

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.ENTERPRISE}
      />
    </>
  );

  const accessDeniedModal = (
    <AccessRestrictedModal
      show={show}
      onClose={close}
      onUpgrade={handleUpgrade}
      t={t}
    />
  );

  return {
    recipientCount,
    canSend,
    isSubmitting,
    isPreviewing: isRequesting,
    accessDenied: !hasMessageAccess,
    favorites,
    banners,
    modals,
    accessDeniedModal,
    submit,
    preview,
    correct,
    saveDefault,
  };
};
